import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import CategoryFilter from '../../components/ui/CategoryFilter';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EBookCard from './components/EBookCard';
import FilterPanel from './components/FilterPanel';
import SortControls from './components/SortControls';
import QuickViewModal from './components/QuickViewModal';

const EBookCatalog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    categories: [],
    frameworks: [],
    expertise: [],
    priceRange: [0, 100]
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('grid');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedEBook, setSelectedEBook] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const mockEBooks = [
    {
      id: 1,
      title: "The Modern Job Search Playbook: Master the art of landing your dream job in today\'s competitive market",
      author: "George Silva",
      description: "A practical, actionable guide designed to help you navigate your job search with greater clarity, confidence, and control. Learn what truly works: how to build relevant job-search skills, reach the right companies, respond effectively to job postings, and position yourself for opportunities that genuinely fit.",
      price: 9.99,
      rating: 4.8,
      reviewCount: 156,
      coverImage: "/assets/images/Book_cover_rev_2-1771510053612.jpeg",
      coverImageAlt: "The Modern Job Search Playbook book cover showing professional career guidance and job hunting strategies for modern job seekers",
      frameworks: ["Resume Building", "Interview Strategies", "Career Planning", "Networking"],
      isNew: true,
      isBestseller: true,
      category: "career-management",
      externalLink: "https://themodernjobsearchplaybook.com/",
      tableOfContents: [
        "Chapter 1: Understanding the Modern Job Market",
        "Chapter 2: Building Relevant Job-Search Skills",
        "Chapter 3: Reaching the Right Companies",
        "Chapter 4: Responding Effectively to Job Postings",
        "Chapter 5: Positioning Yourself for Success",
        "Chapter 6: Interview Preparation and Execution",
        "Chapter 7: Negotiating Your Offer",
        "Chapter 8: Transitioning to Your New Role"
      ]
    },
    {
      id: 2,
      title: "Mastering SWOT Analysis: A Practical Guide for Success - Quick Guide",
      author: "Strategy Shelf",
      description: "This practical guide walks readers through the fundamentals and real world application of SWOT Analysis as a strategic decision making tool. It explains the core concepts of strengths, weaknesses, opportunities, and threats, then provides step by step guidance on how to conduct an effective SWOT analysis using data driven insights. The book includes best practices, common pitfalls to avoid, industry examples, and advanced tips for turning analysis into actionable strategy. Designed for professionals, teams, and leaders, it emphasizes embedding SWOT thinking into everyday business decisions rather than treating it as a one time exercise.",
      price: 4.99,
      rating: 4.7,
      reviewCount: 0,
      coverImage: "/assets/images/Book_Cover-1772192740288.jpg",
      coverImageAlt: "Mastering SWOT Analysis: A Practical Guide for Success book cover showing three professionals collaborating around a laptop with strategy documents",
      frameworks: ["Strategic Planning", "Business Strategy", "Leadership", "Management & Decision Making", "Professional Development"],
      isNew: true,
      isBestseller: false,
      category: "swot-analysis",
      externalLink: "https://thestrategydeskbooks.com/masteringswot",
      tableOfContents: [
        "Chapter 1: Introduction to SWOT Analysis",
        "Chapter 2: Understanding Strengths",
        "Chapter 3: Identifying Weaknesses",
        "Chapter 4: Recognizing Opportunities",
        "Chapter 5: Assessing Threats",
        "Chapter 6: Conducting a Data-Driven SWOT Analysis",
        "Chapter 7: Turning Analysis into Actionable Strategy",
        "Chapter 8: Embedding SWOT Thinking into Business Decisions"
      ]
    },
    {
      id: 3,
      title: "The Lean Team Playbook - Quick Guide",
      author: "Strategy Shelf",
      description: "How Managers Eliminate Waste, Fix Broken Workflows, and Get More Done Without Burnout or Micromanaging",
      price: 4.99,
      rating: 0,
      reviewCount: 0,
      coverImage: "/assets/images/Lean_Team_book_cover-1772275525097.jpg",
      coverImageAlt: "The Lean Team Playbook book cover showing two professionals collaborating at a table, smiling and reviewing documents in a bright office setting",
      frameworks: ["Team Development", "Workflow Optimization", "Leadership", "Management"],
      isNew: true,
      isBestseller: false,
      category: "team-development",
      hasDownload: true,
      downloadUrl: "https://strategyshelf.mysamcart.com/checkout/the-lean-team-playbook",
      externalLink: "https://thestrategydeskbooks.com/lean-team-playbook",
      tableOfContents: [
        "Chapter 1: The Lean Team Mindset",
        "Chapter 2: Identifying and Eliminating Waste",
        "Chapter 3: Fixing Broken Workflows",
        "Chapter 4: Getting More Done Without Burnout",
        "Chapter 5: Leading Without Micromanaging",
        "Chapter 6: Building a High-Performance Team",
        "Chapter 7: Sustaining Lean Practices",
        "Chapter 8: Measuring Team Success"
      ]
    },
    {
      id: 4,
      title: "Welcome to Epic Universe: A Quickstart Guide",
      author: "Strategy Shelf",
      description: "Your Insider Playbook to Universal Orlando's Most Immersive Theme Park Experience Yet",
      price: 4.99,
      rating: 0,
      reviewCount: 0,
      coverImage: "/assets/images/Book_cover-1772282488269.jpg",
      coverImageAlt: "Welcome to Epic Universe: A Quickstart Guide book cover showing an aerial view of Universal Orlando's Epic Universe theme park with the iconic blue dome and Celestial Park fountains",
      frameworks: ["Travel", "Universal Studios", "Theme Parks", "Trip Planning"],
      isNew: true,
      isBestseller: false,
      category: "universal-studios",
      hasDownload: true,
      downloadUrl: "https://strategyshelf.mysamcart.com/checkout/welcome-to-epic-universe-a-quickstart-guide",
      externalLink: "https://thestrategydeskbooks.com/epic-universe",
      tableOfContents: [
        "Chapter 1: Welcome to Epic Universe",
        "Chapter 2: Navigating the Park",
        "Chapter 3: The Five Lands",
        "Chapter 4: Ride Strategy & Crowd Avoidance",
        "Chapter 5: Dining & Snacks",
        "Chapter 6: Using the Universal App",
        "Chapter 7: Insider Tips & Pro Hacks",
        "Chapter 8: Planning Your Perfect Day"
      ]
    },
    {
      id: 5,
      title: "The Art of Engaging Presentations - Quick Guide",
      author: "Strategy Shelf",
      description: "In a world filled with endless meetings, boardroom briefings, and conference presentations, most slides fail to do what they are meant to do: communicate clearly, persuade effectively, and inspire action. The Art of Engaging Presentations is a practical, step by step guide designed to help professionals rethink how they plan, design, and deliver presentations that truly resonate with their audiences.\n\nThis guide begins by addressing a common but costly problem. Most presentations do not fail due to lack of expertise or effort. They fail because they overwhelm audiences with excessive information, lack a clear narrative, and rely on cluttered, text heavy slides that distract rather than clarify. The book reframes presentations not as a design exercise, but as a communication discipline where clarity, structure, and intent matter more than visual flash.\n\nReaders are first guided to define a clear objective and core message before opening PowerPoint. By identifying what the audience should know, feel, or do at the end of the presentation, presenters can ensure every slide serves a purpose. This foundation prevents presentations from becoming a disconnected collection of facts and helps maintain focus throughout.\n\nFrom there, the guide emphasizes planning the story before designing the slides. Drawing on timeless storytelling principles, it shows how to structure a persuasive narrative with a strong opening, a clear problem or opportunity, supporting evidence, and a memorable call to action. Presentations become journeys rather than information dumps, helping audiences stay engaged and follow the message from beginning to end.\n\nA major focus of the book is simplifying complexity. Dense data and technical content are translated into clear insights by prioritizing what matters most, breaking information into digestible pieces, and using plain language and relatable examples. The guide reinforces that simplicity is not about oversimplifying but about distilling complexity so audiences can understand and act.\n\nVisual communication plays a central role. Readers learn how to replace text heavy slides with meaningful visuals such as charts, diagrams, infographics, and photographs that reinforce key points. Practical design principles like alignment, contrast, consistency, and white space are explained in an accessible way, helping presenters create slides that are clean, professional, and easy to follow.\n\nStorytelling and audience centered thinking are woven throughout the guide. By placing the audience first and framing information through relatable stories, presenters can build trust, improve retention, and increase influence. Interactive techniques, clear language, and strategic repetition further strengthen message recall.\n\nUltimately, The Art of Engaging Presentations argues that the goal of any presentation is not applause but action. By prioritizing clarity and credibility over flashy effects, presenters can deliver ideas that inform decisions, drive change, and leave a lasting impact long after the final slide.",
      price: 4.99,
      rating: 0,
      reviewCount: 0,
      coverImage: "/assets/images/Cover_image-1772317584990.jpg",
      coverImageAlt: "The Art of Engaging Presentations book cover showing an auditorium filled with an engaged audience viewed from above, with bold white serif title text overlaid",
      frameworks: ["Business", "Presentations", "Communication", "PowerPoint", "Public Speaking"],
      isNew: true,
      isBestseller: false,
      category: "presentations",
      hasDownload: true,
      downloadUrl: "https://strategyshelf.mysamcart.com/checkout/the-art-of-engaging-presentations",
      externalLink: "https://thestrategydeskbooks.com/engaging-presentations",
      tableOfContents: [
        "Chapter 1: Why Most Presentations Fail",
        "Chapter 2: Define Your Objective and Core Message",
        "Chapter 3: Plan the Story Before the Slides",
        "Chapter 4: Simplifying Complexity",
        "Chapter 5: Visual Communication Principles",
        "Chapter 6: Storytelling and Audience-Centered Thinking",
        "Chapter 7: Design for Clarity",
        "Chapter 8: Delivering with Impact"
      ]
    },
    {
      id: 6,
      title: "Create and Manage the Performance Improvement Plan (PIP) Process",
      subtitle: "A Practical Leadership Guide to Fair, Structured, and Effective Performance Improvement Plan",
      author: "Strategy Shelf",
      description: "Performance Improvement Plans are often misunderstood and frequently misused. Too many organizations treat them as disciplinary tools or legal safeguards rather than as structured opportunities for growth, clarity, and accountability. Create and Manage the Performance Improvement Plan (PIP) Process reframes this approach, presenting PIPs as a leadership discipline rooted in fairness, evidence, and human dignity.\n\nThis guide begins by clarifying the true purpose of a PIP. Rather than serving as a prelude to termination, a well designed PIP creates transparency around expectations, identifies the root causes of performance gaps, and provides employees with a clear path to realignment and recovery. Drawing on organizational psychology and HR best practices, the book emphasizes that successful improvement efforts rely on structure, documentation, and shared ownership between manager and employee.\n\nA central theme throughout the guide is diagnosis before action. Not all performance issues are the same, and treating them uniformly leads to ineffective outcomes. The book walks leaders through identifying early warning signs, assessing whether a PIP is appropriate, and distinguishing between skill gaps, role misalignment, and behavioral challenges. By grounding decisions in observable data rather than assumptions or emotion, managers are better equipped to address the true source of underperformance while preserving trust and credibility.\n\nOnce the need for a PIP is established, the guide outlines how to design one effectively. Readers learn how to set clear, measurable objectives tied to organizational standards and define success metrics that eliminate ambiguity. Emphasis is placed on precision in language, ensuring expectations are observable, trackable, and fair. Timelines are presented not as arbitrary deadlines but as structured checkpoints that balance urgency with realism, reinforcing accountability while allowing time for meaningful change.\n\nEqually important is the human side of the process. The book devotes significant attention to conducting difficult conversations with professionalism and empathy. Leaders are guided on how to prepare for these discussions, manage emotional dynamics, and communicate expectations in ways that reduce defensiveness and encourage ownership. By pairing firm standards with respectful dialogue, the PIP process becomes a collaborative effort rather than a confrontational one.\n\nSustained improvement depends on ongoing support, not one time intervention. The guide details coaching techniques, regular feedback routines, and engagement strategies that help employees take responsibility for their progress. It reinforces that improvement is most durable when employees are active participants in defining obstacles, solutions, and milestones rather than passive recipients of directives.\n\nFinally, the book addresses documentation, legal considerations, and outcome evaluation. Accurate record keeping, consistent tracking methods, and transparent decision making protect both the employee and the organization. Whether a PIP results in renewed performance, role adjustment, or separation, the process ensures outcomes are fair, defensible, and aligned with organizational values.\n\nCreate and Manage the Performance Improvement Plan (PIP) Process equips leaders, HR professionals, and managers with a disciplined, humane framework for addressing performance challenges. When executed with clarity and respect, the PIP becomes not a last resort but a powerful tool for strengthening accountability, capability, and trust across the organization.",
      price: 9.99,
      rating: 0,
      reviewCount: 0,
      coverImage: "/assets/images/Book_image_2-1772321362505.jpg",
      coverImageAlt: "Create and Manage the Performance Improvement Plan (PIP) Process book cover — a leadership guide to fair, structured, and effective performance improvement plans",
      frameworks: ["Business", "People Management", "Leadership", "HR", "Performance Management"],
      isNew: true,
      isBestseller: false,
      category: "people-management",
      hasDownload: true,
      downloadUrl: "https://strategyshelf.mysamcart.com/checkout/create-and-manage-the-performance-improvement-plan-pip-process",
      tableOfContents: [
        "Chapter 1: Reframing the PIP — From Punishment to Partnership",
        "Chapter 2: Diagnosing Performance Issues Before Acting",
        "Chapter 3: Identifying Early Warning Signs",
        "Chapter 4: Designing an Effective PIP",
        "Chapter 5: Setting Clear, Measurable Objectives",
        "Chapter 6: Conducting Difficult Conversations with Empathy",
        "Chapter 7: Coaching, Feedback, and Ongoing Support",
        "Chapter 8: Documentation, Legal Considerations, and Outcome Evaluation"
      ]
    }
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params?.get('search');
    const category = params?.get('category');
    if (search) setSearchQuery(search);
    if (category && category !== 'all') {
      setFilters((prev) => ({ ...prev, categories: [category] }));
    }
  }, [location?.search]);

  const filterEBooks = () => {
    let filtered = [...mockEBooks];

    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(
        (ebook) =>
          ebook?.title?.toLowerCase()?.includes(query) ||
          ebook?.author?.toLowerCase()?.includes(query) ||
          ebook?.description?.toLowerCase()?.includes(query) ||
          ebook?.frameworks?.some((f) => f?.toLowerCase()?.includes(query))
      );
    }

    if (filters?.categories?.length > 0) {
      const selectedCategories = filters?.categories;
      // Map subcategory ids to their parent topic
      const subcategoryToParent = {
        'career-management': 'business',
        'team-development': 'business',
        'swot-analysis': 'business',
        'universal-studios': 'travel',
        'disney-world': 'travel',
        'presentations': 'business',
        'people-management': 'business'
      };
      filtered = filtered?.filter((ebook) => {
        return selectedCategories?.some((cat) => {
          // Direct match on ebook category
          if (ebook?.category === cat) return true;
          // If a parent topic is selected, match all its subcategories
          if (subcategoryToParent?.[ebook?.category] === cat) return true;
          return false;
        });
      });
    }

    if (filters?.frameworks?.length > 0) {
      filtered = filtered?.filter((ebook) =>
        ebook?.frameworks?.some((f) =>
          filters?.frameworks?.some((ff) => f?.toLowerCase()?.includes(ff))
        )
      );
    }

    if (filters?.priceRange) {
      filtered = filtered?.filter(
        (ebook) => ebook?.price >= filters?.priceRange?.[0] && ebook?.price <= filters?.priceRange?.[1]
      );
    }

    switch (sortBy) {
      case 'popularity':
        filtered?.sort((a, b) => b?.reviewCount - a?.reviewCount);
        break;
      case 'newest':
        filtered?.sort((a, b) => (b?.isNew ? 1 : 0) - (a?.isNew ? 1 : 0));
        break;
      case 'price-low':
        filtered?.sort((a, b) => a?.price - b?.price);
        break;
      case 'price-high':
        filtered?.sort((a, b) => b?.price - a?.price);
        break;
      case 'rating':
        filtered?.sort((a, b) => b?.rating - a?.rating);
        break;
      default:
        break;
    }

    return filtered;
  };

  const handleAddToCart = (ebook) => {
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = existingCart?.find((item) => item?.id === ebook?.id);
    let updatedCart;
    if (existingItem) {
      updatedCart = existingCart?.map((item) =>
        item?.id === ebook?.id ? { ...item, quantity: item?.quantity + 1 } : item
      );
    } else {
      updatedCart = [...existingCart, { ...ebook, quantity: 1 }];
    }
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleClearFilters = () => {
    setFilters({ categories: [], frameworks: [], expertise: [], priceRange: [0, 100] });
    setSearchQuery('');
    navigate('/e-book-catalog');
  };

  const activeFilterCount = (filters?.categories?.length || 0) + (filters?.frameworks?.length || 0);
  const filteredEBooks = filterEBooks();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0f172a' }}>
      <Header />
      <CategoryFilter />
      {/* Hero Banner */}
      <div
        className="pt-32 pb-12"
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f2d4a 100%)'
        }}
      >
        <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full"
                  style={{ backgroundColor: 'rgba(234, 179, 8, 0.2)', color: '#fbbf24' }}
                >
                  Strategy Library
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-3">
                Strategy eBooks
              </h1>
              <p className="text-base md:text-lg max-w-2xl" style={{ color: '#94a3b8' }}>
                Discover practical frameworks and actionable insights from industry experts to drive measurable outcomes
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Catalog Body */}
      <main className="pb-16" style={{ backgroundColor: '#0f172a' }}>
        <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-8">

          {/* Filter & Sort Bar */}
          <div
            className="rounded-xl p-4 mb-6 shadow-sm"
            style={{ backgroundColor: '#1e3a5f' }}
          >
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: activeFilterCount > 0 ? '#fbbf24' : 'rgba(255,255,255,0.12)',
                  color: activeFilterCount > 0 ? '#0f172a' : '#e2e8f0'
                }}
              >
                <Icon name="Filter" size={16} />
                Filters
                {activeFilterCount > 0 && (
                  <span
                    className="ml-1 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                    style={{ backgroundColor: '#0f172a', color: '#fbbf24' }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <div className="flex items-center gap-2 flex-1 min-w-[180px] max-w-sm rounded-lg px-3 py-2" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
                <Icon name="Search" size={16} color="#94a3b8" />
                <input
                  type="text"
                  placeholder="Search eBooks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="bg-transparent border-none outline-none text-sm w-full placeholder-slate-400"
                  style={{ color: '#e2e8f0' }}
                />
              </div>
              <div className="flex-1">
                <SortControls
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  resultCount={filteredEBooks?.length}
                  darkMode
                />
              </div>
            </div>
          </div>

          <FilterPanel
            filters={filters}
            onFilterChange={setFilters}
            onClearFilters={handleClearFilters}
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
          />

          {filteredEBooks?.length === 0 ? (
            <div
              className="rounded-xl border p-12 text-center"
              style={{ backgroundColor: '#1e3a5f', borderColor: 'rgba(255,255,255,0.1)' }}
            >
              <Icon name="BookOpen" size={64} color="#94a3b8" className="mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                No eBooks Found
              </h3>
              <p className="mb-6" style={{ color: '#94a3b8' }}>
                Try adjusting your filters or search query to find what you're looking for
              </p>
              <Button variant="default" onClick={handleClearFilters}>
                Clear All Filters
              </Button>
            </div>
          ) : (
            <>
              <div
                className={`grid gap-6 ${
                  viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'
                }`}
              >
                {filteredEBooks?.map((ebook) => (
                  <EBookCard
                    key={ebook?.id}
                    ebook={ebook}
                    onAddToCart={handleAddToCart}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </main>
      <QuickViewModal
        ebook={selectedEBook}
        isOpen={isQuickViewOpen}
        onClose={() => {
          setIsQuickViewOpen(false);
          setSelectedEBook(null);
        }}
        onAddToCart={handleAddToCart}
      />
      {/* Footer */}
      <footer
        className="border-t py-8 px-4 md:px-6"
        style={{ backgroundColor: '#0f172a', borderColor: '#1e3a5f' }}
      >
        <div className="max-w-[1536px] mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img
                src="/assets/images/Logo_final_no_background-1771860991795-1772040132991.png"
                alt="StrategyShelf Logo"
                className="h-8 w-auto object-contain"
              />
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6 text-sm" style={{ color: '#94a3b8' }}>
              <div className="flex items-center gap-2">
                <span>📞 346-523-0048</span>
              </div>
              <div className="flex items-center gap-2">
                <span>✉️ info@thestrategyshelf.com</span>
              </div>
              <button className="hover:text-white transition-smooth">Privacy Policy</button>
              <button className="hover:text-white transition-smooth">Terms of Service</button>
              <button className="hover:text-white transition-smooth">Contact</button>
            </div>
            <p className="text-sm" style={{ color: '#64748b' }}>
              &copy; {new Date()?.getFullYear()} StrategyShelf. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EBookCatalog;